// BUG-2612: one place for the payment-method round-off (the cash tendering aid).
//
// A payment method with roundOffForPos "Y" (integrated or not, BUG-2612) suggests
// the balance rounded to the till's smallest denomination by the currency's
// rounding method (RN nearest, RD down, anything else up); a round-up is the
// order's overPayedAmount. It never changes the order total (BUG-2301 / TIC-9968),
// with one exception (BUG-2612 point 3): a sale settled by that method that
// rounds DOWN puts the difference on total + roundOff (getTenderRoundDown).
// The math here is the exact math the five payment screens carried as copies:
// Math.ceil(remaining / smallestDenomination) * smallestDenomination, and the
// overpayment as a number fixed to the till precision (never a .toFixed string,
// BUG-2103). Each caller keeps its own gate for WHEN to round.

// Smallest denomination of the till's first currency; 1 when there is none.
// Does not sort the till's denomination list in place.
export const getSmallestDenomination = (tillData) => {
  const list = tillData?.tillAccess?.csBunit?.currencies?.[0]?.denominations;
  if (!Array.isArray(list) || list.length === 0) return 1;
  const values = list.map((d) => Number(d?.value)).filter((v) => Number.isFinite(v) && v > 0);
  return values.length > 0 ? Math.min(...values) : 1;
};

export const getPricePrecision = (tillData) => tillData?.tillAccess?.csBunit?.currencies?.[0]?.prcPrecision;

// The gate every payment screen uses. BUG-2612: an integrated method (card
// machine / UPI device) with round-off Y is rounded too; the device is sent the
// rounded amount.
export const isRoundOffPaymentMethod = (pm) => pm?.roundOffForPos === "Y";

export const ceilToDenomination = (amount, denomination) => {
  const denom = Number(denomination) > 0 ? Number(denomination) : 1;
  return Math.ceil(amount / denom) * denom;
};

// BUG-2612 (point 3, round nearest): how a round-off tender rounds. It follows
// the currency's rounding method whether the currency's own POS round-off is on
// (the balance left after a part payment) or off: RN nearest, RD down. NR, RU or
// blank (OFC, KWC) is "RU", the old round up.
export const getTenderRoundingMethod = (tillData) => {
  const currency = tillData?.tillAccess?.csBunit?.currencies?.[0];
  return currency?.roundingMethod === "RN" || currency?.roundingMethod === "RD" ? currency.roundingMethod : "RU";
};

const roundToDenomination = (amount, denomination, method, precision) => {
  if (method !== "RN" && method !== "RD") return ceilToDenomination(amount, denomination);
  const denom = Number(denomination) > 0 ? Number(denomination) : 1;
  const steps = parseFloat((amount / denom).toFixed(6));
  const rounded = (method === "RN" ? Math.round(steps) : Math.floor(steps)) * denom;
  return parseFloat(rounded.toFixed(Number.isFinite(Number(precision)) ? Number(precision) : 2));
};

// remaining: what is still owed (number). Returns the amount to tender as a
// string fixed to precision, the rounded number, the overpayment as a number
// (never below 0) and roundDown: the negative difference when RN / RD rounded
// the balance below what is owed (0 otherwise).
export const computeRoundOffTender = ({ remaining, denomination, precision, method = "RU" }) => {
  const roundedAmount = roundToDenomination(remaining, denomination, method, precision);
  const difference = parseFloat((roundedAmount - remaining).toFixed(precision));
  return {
    roundedAmount,
    amount: roundedAmount.toFixed(precision),
    overPayedAmount: Number.isFinite(difference) && difference > 0 ? difference : 0,
    roundDown: Number.isFinite(difference) && difference < 0 ? difference : 0,
  };
};

// BUG-2612: the rounded tender for the Core billing screen's other routes
// (cart-change effect, Total Amount To Pay reopen, Quick Cash), on every layout:
// a sale (no return, no exchange), a round-off method selected and something
// still owed. Anything else returns null and the caller keeps its old amount.
export const getCoreRoundOffTender = ({ paymentMethod, remaining, isReturn, isExchange, tillData, precision }) => {
  if (isReturn || isExchange) return null;
  if (!isRoundOffPaymentMethod(paymentMethod)) return null;
  const owed = Number(remaining);
  if (!Number.isFinite(owed) || owed <= 0) return null;
  return computeRoundOffTender({
    remaining: owed,
    denomination: getSmallestDenomination(tillData),
    precision: precision ?? getPricePrecision(tillData),
    method: getTenderRoundingMethod(tillData),
  });
};

// BUG-2612 (point 3): a round-off tender that settles a sale (any layout)
// rounded DOWN by the currency method. The bill total cannot stay above
// what was paid (completion and overPayedAmount assume paid >= total), so the
// difference goes on the order the way currency rounding does: total and
// roundOff both move by roundDown (negative). Returns 0 when nothing is rounded
// down: a return or exchange, another method, RU, a balance that rounds up, or
// a tender below the rounded balance (a part payment).
export const getTenderRoundDown = ({ paymentMethod, remaining, value, isReturn, isExchange, tillData, precision }) => {
  if (isReturn || isExchange) return 0;
  if (!isRoundOffPaymentMethod(paymentMethod)) return 0;
  const method = getTenderRoundingMethod(tillData);
  if (method === "RU") return 0;
  const owed = Number(remaining);
  if (!Number.isFinite(owed) || owed <= 0) return 0;
  const tender = computeRoundOffTender({
    remaining: owed,
    denomination: getSmallestDenomination(tillData),
    precision: precision ?? getPricePrecision(tillData),
    method,
  });
  if (tender.roundDown >= 0) return 0;
  if (!(Number(value) >= tender.roundedAmount)) return 0;
  return tender.roundDown;
};

// Applies a round-down from getTenderRoundDown to the cart and records what it
// changed (cashRoundOff) so a delete of that tender can put it back.
export const applyTenderRoundDown = (cart, roundDown, precision) => {
  const total = parseFloat((Number(cart.total) + roundDown).toFixed(precision));
  const roundOff = parseFloat(((Number(cart.roundOff) || 0) + roundDown).toFixed(precision));
  return {
    ...cart,
    total,
    roundOff,
    cashRoundOff: { amount: roundDown, total, prevTotal: cart.total, prevRoundOff: cart.roundOff },
  };
};

// Undo of applyTenderRoundDown when a round-off tender is deleted. Only while
// the cart total is still the rounded one: a cart rebuild (item added, discount)
// recomputes total and roundOff from the lines and the record is then stale.
export const revertTenderRoundDown = (cart, removedPayments) => {
  const record = cart?.cashRoundOff;
  if (!record) return cart;
  if (!(removedPayments || []).some(isRoundOffPaymentMethod)) return cart;
  const next = { ...cart };
  delete next.cashRoundOff;
  if (Number(cart.total) !== Number(record.total)) return next;
  next.total = record.prevTotal;
  next.roundOff = record.prevRoundOff;
  return next;
};
